import { useQuery, useMutation, useQueryClient, useInfiniteQuery, } from '@tanstack/react-query';
import { notifications } from '../api/methods';
/**
 * Notifications React Query hooks (W3-D).
 *
 * Cache shape:
 *   - notificationKeys.list(params)   -> NotificationListResponse (paginated page)
 *   - notificationKeys.unreadCount    -> number (badge polling)
 *
 * The unread-count hook polls every 30s while the tab is visible. React Query
 * defaults `refetchIntervalInBackground` to false, so polling pauses when the
 * window is hidden — no extra wiring required.
 */
export const notificationKeys = {
    all: ['notifications'],
    lists: () => ['notifications', 'list'],
    list: (params) => [
        'notifications',
        'list',
        {
            cursor: params.cursor || null,
            limit: params.limit ?? null,
            unreadOnly: Boolean(params.unreadOnly),
        },
    ],
    infinite: (params) => [
        'notifications',
        'infinite',
        {
            limit: params.limit ?? null,
            unreadOnly: Boolean(params.unreadOnly),
        },
    ],
    unreadCount: ['notifications', 'unread-count'],
};
const UNREAD_POLL_MS = 30_000;
/**
 * Fetch a paginated page of notifications. Pass `limit` and `unreadOnly` to
 * narrow the query; pass `cursor` (an ISO timestamp from a prior page's
 * `nextCursor`) to fetch the next page.
 *
 * Pass `enabled: false` to keep the hook idle (e.g. only fetch while a
 * dropdown is open).
 */
export function useNotifications(params = {}) {
    const { enabled, ...rest } = params;
    return useQuery({
        queryKey: notificationKeys.list(rest),
        queryFn: () => notifications.list(rest),
        enabled: enabled ?? true,
    });
}
/**
 * Cursor-paginated notifications via React Query's useInfiniteQuery.
 * Cache key is shared across pages so mark-read / delete invalidations
 * propagate to the page list immediately.
 */
export function useInfiniteNotifications(params = {}) {
    return useInfiniteQuery({
        queryKey: notificationKeys.infinite(params),
        queryFn: ({ pageParam }) => notifications.list({ ...params, cursor: pageParam }),
        initialPageParam: undefined,
        getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    });
}
/**
 * Unread-count badge query. Polls every 30s while the tab is visible (React
 * Query pauses background polling by default). Also refetches on window focus
 * so users coming back from another tab see fresh counts immediately.
 */
export function useUnreadNotificationCount() {
    return useQuery({
        queryKey: notificationKeys.unreadCount,
        queryFn: () => notifications.unreadCount(),
        refetchInterval: UNREAD_POLL_MS,
        refetchOnWindowFocus: true,
    });
}
function isInfinite(data) {
    return Array.isArray(data.pages);
}
function mapListItems(data, fn) {
    if (isInfinite(data)) {
        return {
            ...data,
            pages: data.pages.map((page) => ({ ...page, items: fn(page.items) })),
        };
    }
    return { ...data, items: fn(data.items) };
}
/**
 * Snapshot every cached notification list, apply `fn` to each one, and return
 * the snapshots so an `onError` handler can roll back. We cancel in-flight list
 * fetches first so a slower refetch can't clobber the optimistic state.
 */
async function applyOptimisticListUpdate(queryClient, fn) {
    await queryClient.cancelQueries({ queryKey: notificationKeys.all });
    const snapshots = queryClient.getQueriesData({
        queryKey: notificationKeys.all,
    });
    for (const [key, data] of snapshots) {
        if (data)
            queryClient.setQueryData(key, mapListItems(data, fn));
    }
    return snapshots.filter((entry) => Boolean(entry[1]));
}
function rollbackLists(queryClient, snapshots) {
    if (!snapshots)
        return;
    for (const [key, data] of snapshots) {
        queryClient.setQueryData(key, data);
    }
}
export function useMarkNotificationRead() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id) => notifications.markRead(id),
        onMutate: async (id) => {
            const now = new Date().toISOString();
            const prevUnread = queryClient.getQueryData(notificationKeys.unreadCount);
            // Optimistically stamp read_at so the row drops its unread styling now.
            const lists = await applyOptimisticListUpdate(queryClient, (items) => items.map((n) => n.id === id && !n.read_at ? { ...n, read_at: now } : n));
            if (typeof prevUnread === 'number') {
                queryClient.setQueryData(notificationKeys.unreadCount, Math.max(0, prevUnread - 1));
            }
            return { lists, prevUnread };
        },
        onError: (_err, _id, ctx) => {
            rollbackLists(queryClient, ctx?.lists);
            if (ctx && typeof ctx.prevUnread === 'number') {
                queryClient.setQueryData(notificationKeys.unreadCount, ctx.prevUnread);
            }
        },
        onSettled: () => {
            // Reconcile with the server (covers read_at value + badge drift).
            queryClient.invalidateQueries({ queryKey: notificationKeys.all });
        },
    });
}
export function useMarkAllNotificationsRead() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: () => notifications.markAllRead(),
        onMutate: async () => {
            const now = new Date().toISOString();
            const prevUnread = queryClient.getQueryData(notificationKeys.unreadCount);
            const lists = await applyOptimisticListUpdate(queryClient, (items) => items.map((n) => (n.read_at ? n : { ...n, read_at: now })));
            queryClient.setQueryData(notificationKeys.unreadCount, 0);
            return { lists, prevUnread };
        },
        onError: (_err, _vars, ctx) => {
            rollbackLists(queryClient, ctx?.lists);
            if (ctx && typeof ctx.prevUnread === 'number') {
                queryClient.setQueryData(notificationKeys.unreadCount, ctx.prevUnread);
            }
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: notificationKeys.all });
        },
    });
}
export function useDeleteNotification() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id) => notifications.remove(id),
        onMutate: async (id) => {
            const prevUnread = queryClient.getQueryData(notificationKeys.unreadCount);
            // Was the deleted row unread? If so, drop the badge by one optimistically.
            let removedUnread = false;
            const lists = await applyOptimisticListUpdate(queryClient, (items) => items.filter((n) => {
                if (n.id !== id)
                    return true;
                if (!n.read_at)
                    removedUnread = true;
                return false;
            }));
            if (removedUnread && typeof prevUnread === 'number') {
                queryClient.setQueryData(notificationKeys.unreadCount, Math.max(0, prevUnread - 1));
            }
            return { lists, prevUnread };
        },
        onError: (_err, _id, ctx) => {
            rollbackLists(queryClient, ctx?.lists);
            if (ctx && typeof ctx.prevUnread === 'number') {
                queryClient.setQueryData(notificationKeys.unreadCount, ctx.prevUnread);
            }
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: notificationKeys.all });
        },
    });
}
//# sourceMappingURL=useNotifications.js.map